import type { LeaderboardEntryDto } from '@vmt/shared';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { percent } from '../lib/format';
import { RankIcon } from './RankBadge';

export function LeaderboardTable({
  entries,
  compact,
}: {
  entries: LeaderboardEntryDto[];
  compact?: boolean;
}) {
  const showDetails = entries.some((e) => e.details);
  return (
    <div className="overflow-x-auto">
      <table
        className={clsx(
          'w-full text-sm',
          showDetails && !compact ? 'min-w-[760px]' : 'min-w-[480px]',
        )}
      >
        <thead>
          <tr className="border-b border-ink-700 text-left text-[11px] tracking-wider text-ink-400 uppercase">
            <th className="w-10 py-2 pl-4">#</th>
            <th className="py-2">Spieler</th>
            <th className="py-2 text-right">Serien</th>
            <th className="py-2 text-right">Maps</th>
            <th className="py-2 text-right">Map-WR</th>
            {!compact && <th className="py-2 text-right">Runden</th>}
            {!compact && <th className="py-2 text-right">K/D</th>}
            {!compact && showDetails && (
              <>
                <th className="py-2 text-right" title="Schaden pro Runde (importierte Maps)">
                  ADR
                </th>
                <th className="py-2 text-right">HS%</th>
                <th className="py-2 text-right" title="First Kills">
                  FK
                </th>
                <th className="py-2 text-right">Clutches</th>
              </>
            )}
            <th className="py-2 pr-4 text-right">Ø ACS</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={e.playerId} className="border-b border-ink-800 last:border-0 hover:bg-ink-850">
              <td
                className={clsx(
                  'py-2.5 pl-4 font-display text-base font-bold',
                  i < 3 ? 'text-accent' : 'text-ink-500',
                )}
              >
                {i + 1}
              </td>
              <td className="py-2.5">
                <Link
                  to={`/players/${e.playerId}`}
                  className="flex items-center gap-2 font-medium hover:text-accent"
                >
                  <RankIcon tier={e.currentRankTier} className="size-6" />
                  <span className="truncate">{e.displayName}</span>
                </Link>
              </td>
              <td className="py-2.5 text-right tabular-nums">
                {e.seriesWon}
                <span className="text-ink-500">/{e.seriesPlayed}</span>
              </td>
              <td className="py-2.5 text-right tabular-nums">
                {e.mapsWon}
                <span className="text-ink-500">/{e.mapsPlayed}</span>
              </td>
              <td className="py-2.5 text-right tabular-nums">{percent(e.mapWinrate)}</td>
              {!compact && (
                <td className="py-2.5 text-right tabular-nums">
                  {e.roundsWon}
                  <span className="text-ink-500">:{e.roundsLost}</span>
                </td>
              )}
              {!compact && (
                <td className="py-2.5 text-right tabular-nums">
                  {e.kd?.toLocaleString('de-DE') ?? '–'}
                </td>
              )}
              {!compact && showDetails && (
                <>
                  <td className="py-2.5 text-right tabular-nums">{e.details?.adr ?? '–'}</td>
                  <td className="py-2.5 text-right tabular-nums">
                    {e.details?.headshotPercent != null
                      ? Math.round(e.details.headshotPercent)
                      : '–'}
                  </td>
                  <td className="py-2.5 text-right tabular-nums">{e.details?.firstKills ?? '–'}</td>
                  <td className="py-2.5 text-right tabular-nums">{e.details?.clutches ?? '–'}</td>
                </>
              )}
              <td className="py-2.5 pr-4 text-right tabular-nums">{e.avgAcs ?? '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
